import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { store } from "./shared/redux/store.ts";
import { Provider } from "react-redux";
import { Auth0Provider } from "@auth0/auth0-react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import System from "./features/system/System";
import TenantOnboarding from "./features/tenant/onboarding/Onboarding";
import { OnboardingDetail } from "./features/tenant/onboarding/OnboardingDetail";
import TESASupplement from "./features/tenant/onboarding/TESASupplement";
import Tenants from "./features/tenant/tenant/Tenants";
import Tenant from "./features/tenant/tenant/Tenant";
import EditTenant from "./features/tenant/tenant/EditTenant";
import Impersonate from "./features/impersonate/Impersonate";
import Invoices from "./features/collections/invoices/Invoices";
import Payments from "./features/collections/payments/Payments";
import FileExplorer from "./features/file_explorer/FileExplorer";
import Programs from "./features/programs/Programs";
import Program from "./features/programs/Program";
import UsersPage from "./features/users/pages/users/UsersPage.tsx";
import UserPage from "./features/users/pages/user/UserPage.tsx";
import TenantUsersPage from "./features/users/pages/tenant-users/TenantUsersPage.tsx";
import App from "./App.tsx";
import NoteSearch from './features/notes/Notes.tsx'
import BaseCaseModelPage from './features/admin/baseCaseModel/index.tsx'
import EventHandlingReport from './features/admin/eventHandlingReport/EventHandlingReport.tsx'
import GoLiveReadiness from './features/admin/goLiveReadiness/index.tsx'
import Tariffs from './features/tariffs/Tariffs.tsx'
import Tariff from './features/tariffs/Tariff.tsx'
import TariffRateValidation from './features/tariffs/TariffRateValidation.tsx'
import AccRates from './features/tariffs/AccRates.tsx'
import ReconciliationReport from './features/programs/reconciliation/ReconciliationReport.tsx'
import Saber from './features/saber/Saber.tsx'
import Conductor from './features/conductor/Conductor.tsx'
import { setupSentry } from "./util/sentry.ts";

// Initialize Sentry
setupSentry();

const {
    VITE_AUTH0_DOMAIN: AUTH0_DOMAIN,
    VITE_AUTH0_CLIENT_ID: AUTH0_CLIENT_ID,
    VITE_AUTH0_AUDIENCE: AUTH0_AUDIENCE,
    VITE_AUTH0_SCOPE: AUTH0_SCOPE,
} = import.meta.env

const router = createBrowserRouter([
    {
        path: '/impersonate',
        element: <Impersonate />,
        errorElement: <ErrorBoundary />,
    },
    {
        path: '/tenant/onboarding/TESAsupplement/:agreementId',
        element: <TESASupplement />,
        errorElement: <ErrorBoundary />,
    },
    {
        path: '/tenant/onboarding/TESA/:agreementId',
        element: <TESASupplement />,
        errorElement: <ErrorBoundary />,
    },
    {
        path: '/',
        element: <App />,
        errorElement: <ErrorBoundary />,
        children: [
            {
                path: '/',
                element: <System />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/tenant/onboarding',
                element: <TenantOnboarding />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/tenant/onboarding/:registrationId',
                element: <OnboardingDetail />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/tenant/tenants',
                element: <Tenants />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/tenant/tenants',
                element: <Tenants />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/tenant/tenants/:tenantId',
                element: <Tenant />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/tenant/tenants/:tenantId/edit',
                element: <EditTenant />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/collections/payments',
                element: <Payments />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/collections/invoices',
                element: <Invoices />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/users',
                element: <UsersPage />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/users/:userID',
                element: <UserPage />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/tenant-users',
                element: <TenantUsersPage />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/file-explorer',
                element: <FileExplorer />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/program/programs',
                element: <Programs />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/program/programs/:programId',
                element: <Program />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/program/programs/:programId/reconciliation',
                element: <ReconciliationReport />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/notes',
                element: <NoteSearch />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/admin/base-case-model',
                element: <BaseCaseModelPage />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/admin/event-handling-report',
                element: <EventHandlingReport />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/admin/go-live-readiness',
                element: <GoLiveReadiness />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/admin/tariffs',
                element: <Tariffs />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/admin/tariffs/rate-validation',
                element: <TariffRateValidation />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/admin/tariffs/acc-rates',
                element: <AccRates />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/admin/tariffs/:tariffId',
                element: <Tariff />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/saber/*',
                element: <Saber />,
                errorElement: <ErrorBoundary />,
            },
            {
                path: '/conductor/*',
                element: <Conductor />,
                errorElement: <ErrorBoundary />,
            },
        ],
    },
])

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <Provider store={store}>
            <Auth0Provider
                domain={AUTH0_DOMAIN}
                clientId={AUTH0_CLIENT_ID}
                authorizationParams={{
                    audience: AUTH0_AUDIENCE,
                    scope: AUTH0_SCOPE,
                    redirect_uri: window.location.origin,
                }}
                cacheLocation="localstorage"
                // Pairs with offline_access on VITE_AUTH0_SCOPE. Fallback uses the Auth0 session when no refresh token is cached.
                useRefreshTokens
                useRefreshTokensFallback
            >
                <RouterProvider router={router} />
            </Auth0Provider>
        </Provider>
    </React.StrictMode>
);
